import type { BRepRegistrar } from '../../worker'
import { applyImportedModelTopologyRuntime } from '../topology/imported-model-topology-runtime'
import {
  normalizeImportedAssetFileId,
  parseImportedBrepSidecar,
  resolveImportedBrepSidecarEntry,
  resolveImportedBrepSidecarFileId,
  type ImportedBrepSidecarDocument,
} from '../sidecar'
import type {
  ImportedBrepRef,
  ImportedTopologySource,
  PartAsset,
} from '../loaders/types'
import {
  enrichBodyEntriesWithModelBodyIdentities,
  enrichSceneBodyEntriesWithModelBodyIdentities,
} from '../loaders/enrich-body-entry-identities'
import { createImportedBrepRuntimeCache, type ImportedBrepRuntimeCache } from './ImportedBrepRuntimeCache'
import { ImportedBrepHydrationError } from './ImportedBrepHydrationError'
import {
  buildImportedBrepExportDefinitionId,
  buildImportedBrepGeometryGroupKey,
  buildImportedBrepGeometryObjectId,
} from './imported-brep-geometry-object-id'
import {
  hydrateImportedTopologySourceTopoRuntime,
  type ImportedTopoHydrationResult,
} from './hydrateImportedTopologySourceTopoRuntime'
import { resolveImportedTopologySidecarFileId } from './imported-sidecar-file-lookup'
import { formatImportedTopologyHydrationDiagnostic } from './imported-topology-hydration-diagnostics'

/**
 * sidecar 文本解码器。
 */
const importedSidecarTextDecoder = new TextDecoder()

/**
 * 具备 BRep payload 的 body 绑定。
 */
type ImportedBrepBackedBodyBinding =
  NonNullable<ImportedTopologySource['bodyBrepBindings']>[string] & { brepRef: ImportedBrepRef }

/**
 * 判断 body 绑定是否具备 BRep Shape 注册来源。
 *
 * @param binding body 绑定。
 * @returns 是否具备 BRep 引用。
 */
function hasImportedBrepRef(
  binding: NonNullable<ImportedTopologySource['bodyBrepBindings']>[string],
): binding is ImportedBrepBackedBodyBinding {
  return Boolean(binding.brepRef)
}

/**
 * 导入资产文件的最小描述。
 *
 * 设计说明：
 * 1. cad-core 只依赖 sidecar 解析真正需要的最小文件字段；
 * 2. 这样宿主可传入 project file、临时插入文件或测试桩，而不把具体 DTO 耦合到 SDK；
 * 3. 下载或读取文件的实现由宿主注入，cad-core 只负责业务编排。
 */
export interface ImportedAssetFileDescriptor {
  fileId: string
  uuid?: string
  kind?: string
}

/**
 * 导入 BRep Shape 注册日志钩子。
 */
export interface ImportedBrepHydrationLogger {
  onWarn?: (message: string) => void
  onError?: (message: string, error: unknown) => void
}

/**
 * 导入拓扑源 BRep Shape 注册运行时依赖。
 */
export interface ImportedTopologySourceBrepHydrationOptions<TFile extends ImportedAssetFileDescriptor> {
  projectFiles: TFile[]
  readFileBytes: (file: TFile) => Promise<Uint8Array>
  brepRegistrar?: BRepRegistrar
  importedBrepRuntimeCache?: ImportedBrepRuntimeCache
  assertCurrent?: () => void
  logger?: ImportedBrepHydrationLogger
}

/**
 * 获取运行时缓存。
 *
 * @param importedBrepRuntimeCache 外部注入缓存。
 * @returns 可用缓存实例。
 */
function resolveImportedBrepRuntimeCache(
  importedBrepRuntimeCache: ImportedBrepRuntimeCache | undefined,
): ImportedBrepRuntimeCache {
  return importedBrepRuntimeCache ?? createImportedBrepRuntimeCache()
}

/**
 * 在项目文件列表中查找 sidecar 文件。
 *
 * @param projectFiles 项目文件列表。
 * @param sidecarFileId sidecar 文件 ID。
 * @returns 命中的文件描述；未命中时返回 `null`。
 */
function findImportedSidecarFile<TFile extends ImportedAssetFileDescriptor>(
  projectFiles: TFile[],
  sidecarFileId: string,
): TFile | null {
  const normalizedTargetFileId = normalizeImportedAssetFileId(sidecarFileId)
  return (
    projectFiles.find(
      (projectFile) => normalizeImportedAssetFileId(projectFile.fileId) === normalizedTargetFileId,
    ) ?? null
  )
}

/**
 * 触发可选的“当前请求仍有效”断言。
 *
 * @param assertCurrent 当前请求断言函数。
 */
function assertImportedBrepHydrationCurrent(assertCurrent?: () => void): void {
  assertCurrent?.()
}

/**
 * 记录 error。
 *
 * @param logger 日志钩子。
 * @param message 日志内容。
 * @param error 错误对象。
 */
function reportImportedBrepHydrationError(
  logger: ImportedBrepHydrationLogger | undefined,
  message: string,
  error: unknown,
): void {
  logger?.onError?.(message, error)
}

/**
 * 收集并上报一个可定位到具体 body 的严格 hydration 失败。
 *
 * @param failures 当前拓扑源已收集的失败。
 * @param logger 日志钩子。
 * @param diagnostic 包含文件、body 与 sidecar 协议信息的诊断。
 * @param cause 底层读取、解析或 Worker 注册错误。
 */
function appendImportedBrepHydrationFailure(
  failures: Error[],
  logger: ImportedBrepHydrationLogger | undefined,
  diagnostic: string,
  cause?: unknown,
): void {
  const causeMessage = cause instanceof Error
    ? cause.message
    : cause === undefined
      ? undefined
      : String(cause)
  const failure = new Error(causeMessage ? `${diagnostic}; cause=${causeMessage}` : diagnostic, {
    cause,
  })
  failures.push(failure)
  reportImportedBrepHydrationError(logger, failure.message, failure)
}

/**
 * 解析导入拓扑补齐所需的 BRep Shape 注册能力。
 *
 * @param options 运行时依赖。
 * @returns BRep Shape 注册能力。
 */
function requireImportedBrepRegistrar<TFile extends ImportedAssetFileDescriptor>(
  options: ImportedTopologySourceBrepHydrationOptions<TFile>,
): BRepRegistrar & {
  registerImportedBrepShapesAtomically: NonNullable<BRepRegistrar['registerImportedBrepShapesAtomically']>
} {
  if (options.brepRegistrar?.registerImportedBrepShapesAtomically) {
    return options.brepRegistrar as BRepRegistrar & {
      registerImportedBrepShapesAtomically: NonNullable<BRepRegistrar['registerImportedBrepShapesAtomically']>
    }
  }

  const registrarFailure = new Error(
    'hydrateImportedTopologySourceBrepRuntime 缺少支持原子批量注册的 brepRegistrar，无法注册 BRep Shape',
  )
  const error = new ImportedBrepHydrationError([registrarFailure])
  reportImportedBrepHydrationError(
    options.logger,
    '缺少 BRep Shape 注册能力，无法注册 STEP 导出 Shape',
    error,
  )
  throw error
}

/**
 * 按 sidecar 文件 ID 对 body 绑定分组。
 *
 * @param file 当前 GLB 文件。
 * @param brepBindings body 绑定集合。
 * @returns 分组后的 body 绑定。
 */
function groupImportedBrepBindingsBySidecarFileId(
  file: ImportedAssetFileDescriptor,
  brepBindings: ImportedBrepBackedBodyBinding[],
): Map<string, ImportedBrepBackedBodyBinding[]> {
  const groupedBindings = new Map<string, ImportedBrepBackedBodyBinding[]>()

  brepBindings.forEach((brepBinding) => {
    const sidecarFileId = resolveImportedBrepSidecarFileId(file.fileId, brepBinding.brepRef)
    const currentBucket = groupedBindings.get(sidecarFileId)
    if (currentBucket) {
      currentBucket.push(brepBinding)
      return
    }
    groupedBindings.set(sidecarFileId, [brepBinding])
  })

  return groupedBindings
}

/**
 * 解析当前 body 的 topo sidecar 状态，用于 Shape 注册失败诊断。
 *
 * @param binding 当前 body 绑定。
 * @param topoHydrationResult topo sidecar hydration 结果。
 * @returns topo sidecar 状态说明。
 */
function resolveImportedTopoShapeRegistrationContext(
  binding: NonNullable<ImportedTopologySource['bodyBrepBindings']>[string],
  topoHydrationResult: ImportedTopoHydrationResult,
): string {
  if (!binding.topologyRef) {
    return '当前 body 未声明 topologyRef'
  }
  if (topoHydrationResult.hydratedBodyBindingIds.has(binding.bodyBindingId)) {
    return 'topo sidecar 已生成交互拓扑，BRep Shape 注册失败'
  }
  return topoHydrationResult.unavailableReasonsByBodyBindingId.get(binding.bodyBindingId)
    ?? 'topo sidecar 未生成当前 body 的拓扑资产'
}

/**
 * 判断当前输入是否具备注册 BRep Shape 的理论条件。
 *
 * 设计说明：
 * 1. 该预检必须发生在 topo sidecar hydration 之前，避免缺少 registrar 时先读取 sidecar 并污染拓扑源；
 * 2. 这里只根据输入拓扑源判断，已有 topo 资产或 topologyRef 都表示后续可能进入 Shape 注册链路；
 * 3. 不在这里读取 sidecar，也不推断 sidecar 是否一定成功，失败细节仍交给原有 topo hydration 诊断。
 *
 * @param topologySource 导入拓扑源。
 * @returns 是否需要在读取 sidecar 前确认 BRep registrar 已注入。
 */
function shouldPreflightImportedBrepRegistrar(
  topologySource: ImportedTopologySource,
): boolean {
  return Object.values(topologySource.bodyBrepBindings ?? {}).some((binding) => (
    hasImportedBrepRef(binding) && !binding.deferBodyBrepMatch
  ))
}

/**
 * 解析当前 body 的 topo sidecar 文件 ID。
 *
 * @param file 当前 GLB 文件。
 * @param binding 当前 body 绑定。
 * @returns 已解析 topo sidecar 文件 ID。
 */
function resolveImportedTopologyDiagnosticFileId(
  file: ImportedAssetFileDescriptor,
  binding: NonNullable<ImportedTopologySource['bodyBrepBindings']>[string],
): string | undefined {
  return binding.topologyRef
    ? resolveImportedTopologySidecarFileId(file.fileId, binding.topologyRef)
    : undefined
}

/**
 * 读取并解析 sidecar 文档。
 *
 * @param sidecarFile sidecar 文件。
 * @param runtimeCache 运行时缓存。
 * @param readFileBytes 文件读取器。
 * @returns sidecar 文档。
 */
async function loadImportedSidecarDocument<TFile extends ImportedAssetFileDescriptor>(
  sidecarFile: TFile,
  runtimeCache: ImportedBrepRuntimeCache,
  readFileBytes: (file: TFile) => Promise<Uint8Array>,
): Promise<ImportedBrepSidecarDocument> {
  const normalizedSidecarFileId = normalizeImportedAssetFileId(sidecarFile.fileId)
  return runtimeCache.getOrCreateSidecarDocument(normalizedSidecarFileId, async () => {
    const sidecarBytes = await readFileBytes(sidecarFile)
    return parseImportedBrepSidecar(importedSidecarTextDecoder.decode(sidecarBytes))
  })
}

/**
 * 解析导入 BRep 来源层级优先级。
 *
 * @param sourceLevel BRep 引用来源层级。
 * @returns 数值越大表示越接近真实 body 语义。
 */
function resolveImportedBrepBindingPriority(sourceLevel: string | undefined): number {
  if (sourceLevel === 'body') return 3
  if (sourceLevel === 'entity') return 2
  if (sourceLevel === 'mesh') return 1
  return 0
}

/**
 * 从同一语义 body 的多个可见绑定中选择权威 BRep。
 *
 * @param bindings 同一语义 body 的候选绑定。
 * @returns 用于恢复 live Puppy CAD 内核 shape 的主绑定。
 */
function pickPrimaryImportedBrepBinding(
  bindings: ImportedBrepBackedBodyBinding[],
): ImportedBrepBackedBodyBinding {
  const selectorContracts = new Set(bindings.map((binding) => [
    binding.brepRef.sha256,
    binding.brepBodyOrdinal ?? '',
    binding.brepBodyKind ?? '',
  ].join('|')))
  if (selectorContracts.size > 1) {
    throw new Error(
      `同一导入 Body 存在冲突的 BRep 来源信息: bodyId=${bindings[0]?.bodyId ?? '缺失'}`,
    )
  }

  return [...bindings].sort((left, right) => {
    const priorityDelta = resolveImportedBrepBindingPriority(right.sourceLevel)
      - resolveImportedBrepBindingPriority(left.sourceLevel)
    if (priorityDelta !== 0) {
      return priorityDelta
    }
    return (right.brepRef.byteLength ?? 0) - (left.brepRef.byteLength ?? 0)
  })[0]!
}

/**
 * 按建模/导出语义对 body BRep 绑定去重。
 *
 * 设计说明：
 * GLB 可见 mesh 可能把一个 SolidWorks 实体拆成很多 primitive。这里按导入文件作用域
 * 与 bodyId 去重，缺少 bodyId 时才退回 BRep sha，避免把可见网格数量误当成实体数量。
 *
 * @param file 当前 GLB 文件。
 * @param bindings 当前 sidecar 文件下的 body 绑定。
 * @returns 去重后的语义 body 分组。
 */
function groupImportedBrepBindingsByGeometryObject(
  file: ImportedAssetFileDescriptor,
  bindings: ImportedBrepBackedBodyBinding[],
): Array<{
  geometryObjectId: string
  exportDefinitionId: string
  bindings: ImportedBrepBackedBodyBinding[]
  primaryBinding: ImportedBrepBackedBodyBinding
}> {
  const groupsByKey = new Map<string, ImportedBrepBackedBodyBinding[]>()
  bindings.forEach((binding) => {
    const key = buildImportedBrepGeometryGroupKey(file, binding)
    const group = groupsByKey.get(key)
    if (group) {
      group.push(binding)
      return
    }
    groupsByKey.set(key, [binding])
  })

  return Array.from(groupsByKey.values()).map((groupBindings) => {
    const primaryBinding = pickPrimaryImportedBrepBinding(groupBindings)
    return {
      geometryObjectId: buildImportedBrepGeometryObjectId(file, primaryBinding),
      exportDefinitionId: buildImportedBrepExportDefinitionId(primaryBinding),
      bindings: groupBindings,
      primaryBinding,
    }
  })
}

/**
 * 解析导入 body 在建模状态中的显示名称。
 *
 * @param topologySource 导入拓扑源。
 * @param binding body 级 BRep 绑定。
 * @param fallbackName 兜底文件名。
 * @returns 可写入 GeometryState 与 STEP 的名称。
 */
function resolveImportedBrepGeometryName(
  topologySource: ImportedTopologySource,
  binding: NonNullable<ImportedTopologySource['bodyBrepBindings']>[string],
  fallbackName: string,
): string {
  const bodyEntry = binding.bodyId
    ? topologySource.bodyEntries.find((entry) => entry.bodyId === binding.bodyId)
    : undefined
  if (bodyEntry?.label && bodyEntry.label.trim().length > 0) {
    return bodyEntry.label.trim()
  }

  const meshName = topologySource.meshCatalog[binding.meshId]?.objectName
  if (meshName && meshName.trim().length > 0) {
    return meshName.trim()
  }

  return fallbackName
}

/**
 * 为零件资产补齐 STEP 导出所需的 BRep Shape 注册。
 *
 * @param file 当前 GLB 文件。
 * @param asset 已解析零件资产。
 * @param options 运行时依赖。
 */
export async function hydrateImportedPartAssetBrepRuntime<TFile extends ImportedAssetFileDescriptor>(
  file: TFile,
  asset: PartAsset,
  options: ImportedTopologySourceBrepHydrationOptions<TFile>,
): Promise<void> {
  await hydrateImportedTopologySourceBrepRuntime(file, asset, options)
  enrichSceneBodyEntriesWithModelBodyIdentities(
    asset.renderTemplate,
    asset.bodyBrepBindings ?? {},
    asset.bodyTopologyAssets ?? {},
  )
}

/**
 * 为任意导入拓扑源补齐 STEP 导出所需的 BRep Shape 注册。
 *
 * 设计说明：
 * 1. topo sidecar 负责交互拓扑，BRep sidecar 只负责注册 Worker 内真实 Puppy CAD 内核 Shape；
 * 2. 宿主只负责提供文件读取适配器与生命周期钩子，避免把协议判断散落到 UI 层；
 * 3. 只有显式声明 `brepRef` 的 body 才进入严格 Shape 契约；合法 topo-only body 仍可只恢复交互拓扑；
 * 4. 全部 topo/BRep sidecar 会先完成预校验，再通过 Worker 原子批量入口注册 Shape；
 * 5. topo 资产只在全部严格 Shape 注册成功后一次提交，避免场景形成不可保存的部分成功状态。
 *
 * @param file 当前 GLB 文件。
 * @param topologySource 目标导入拓扑源。
 * @param options 运行时依赖。
 * @throws 当交互拓扑、BRep sidecar 预校验或 Worker 原子注册失败，以及运行时 current guard 判定当前加载已过期时抛出错误。
 */
export async function hydrateImportedTopologySourceBrepRuntime<TFile extends ImportedAssetFileDescriptor>(
  file: TFile,
  topologySource: ImportedTopologySource,
  options: ImportedTopologySourceBrepHydrationOptions<TFile>,
): Promise<void> {
  const bodyBindings = Object.values(topologySource.bodyBrepBindings ?? {})
  if (bodyBindings.length === 0) {
    return
  }

  const brepBindings = bodyBindings.filter(hasImportedBrepRef)
  const preflightBrepRegistrar = shouldPreflightImportedBrepRegistrar(topologySource)
    ? requireImportedBrepRegistrar(options)
    : undefined
  const topoHydrationResult = await hydrateImportedTopologySourceTopoRuntime(file, topologySource, options)
  const bodyTopologyAssets = topoHydrationResult.bodyTopologyAssets
  const failures: Error[] = []

  // 第一阶段只校验 brepRef-backed body 的交互拓扑；topo-only body 不承担可保存 Shape 契约。
  brepBindings.forEach((binding) => {
    const topologyAsset = bodyTopologyAssets[binding.bodyBindingId]
    if (!topologyAsset) {
      const unavailableReason = topoHydrationResult.unavailableReasonsByBodyBindingId.get(binding.bodyBindingId)
        ?? (binding.topologyRef
          ? 'topo sidecar 未生成当前 body 的拓扑资产'
          : '当前 body 未声明 topologyRef 且不存在可用拓扑资产')
      appendImportedBrepHydrationFailure(
        failures,
        options.logger,
        `声明的可见 BRep body 缺少交互拓扑: ${formatImportedTopologyHydrationDiagnostic({
          file,
          topologySource,
          binding,
          reason: unavailableReason,
          topologySidecarFileId: resolveImportedTopologyDiagnosticFileId(file, binding),
          brepSidecarFileId: resolveImportedBrepSidecarFileId(file.fileId, binding.brepRef),
        })}`,
      )
    }
  })

  if (failures.length > 0) {
    throw new ImportedBrepHydrationError(failures)
  }

  if (brepBindings.length === 0) {
    applyImportedModelTopologyRuntime(topologySource, bodyTopologyAssets)
    topologySource.bodyEntries = enrichBodyEntriesWithModelBodyIdentities(
      topologySource.bodyEntries,
      topologySource.bodyBrepBindings ?? {},
      bodyTopologyAssets,
    )
    return
  }

  const runtimeCache = resolveImportedBrepRuntimeCache(options.importedBrepRuntimeCache)
  const groupedBindings = groupImportedBrepBindingsBySidecarFileId(file, brepBindings)
  const registrationPlans: Array<{
    geometryGroup: ReturnType<typeof groupImportedBrepBindingsByGeometryObject>[number]
    payload: RegisterImportedBrepShapePayload
    resolvedEncoding: string
  }> = []

  // 第二阶段解析全部 BRep sidecar 与 entry；此阶段仍不进入 Worker，保证数据错误不会造成部分注册。
  for (const [sidecarFileId, sidecarBindings] of groupedBindings.entries()) {
    assertImportedBrepHydrationCurrent(options.assertCurrent)

    const sidecarFile = findImportedSidecarFile(options.projectFiles, sidecarFileId)
    if (!sidecarFile) {
      sidecarBindings.forEach((binding) => {
        appendImportedBrepHydrationFailure(
          failures,
          options.logger,
          `BRep sidecar 文件未找到，无法注册 STEP 导出 Shape: ${formatImportedTopologyHydrationDiagnostic({
            file,
            topologySource,
            binding,
            reason: `BRep sidecar 文件未找到，无法注册 STEP 导出 Shape: ${sidecarFileId}`,
            topologySidecarFileId: resolveImportedTopologyDiagnosticFileId(file, binding),
            brepSidecarFileId: sidecarFileId,
          })}`,
        )
      })
      continue
    }

    let sidecarDocument: ImportedBrepSidecarDocument
    try {
      sidecarDocument = await loadImportedSidecarDocument(
        sidecarFile,
        runtimeCache,
        options.readFileBytes,
      )
    } catch (error) {
      sidecarBindings.forEach((binding) => {
        appendImportedBrepHydrationFailure(
          failures,
          options.logger,
          `BRep sidecar 文档读取失败: ${formatImportedTopologyHydrationDiagnostic({
            file,
            topologySource,
            binding,
            reason: `BRep sidecar 文档读取失败: ${sidecarFileId}`,
            topologySidecarFileId: resolveImportedTopologyDiagnosticFileId(file, binding),
            brepSidecarFileId: sidecarFileId,
          })}`,
          error,
        )
      })
      continue
    }
    assertImportedBrepHydrationCurrent(options.assertCurrent)

    const geometryGroups = groupImportedBrepBindingsByGeometryObject(file, sidecarBindings)

    for (const geometryGroup of geometryGroups) {
      assertImportedBrepHydrationCurrent(options.assertCurrent)
      const { primaryBinding } = geometryGroup
      let resolvedBrepEncoding = primaryBinding.brepRef.encoding

      try {
        const sidecarEntry = await resolveImportedBrepSidecarEntry(sidecarDocument, primaryBinding.brepRef)
        resolvedBrepEncoding = sidecarEntry.encoding
        if (primaryBinding.sourceLevel === 'mesh' && primaryBinding.bodyId && primaryBinding.deferBodyBrepMatch) {
          // 加载只保存已校验原文；没有执行 Body 匹配就不能发布 geometryObjectId。
          for (const binding of geometryGroup.bindings) {
            const asset = bodyTopologyAssets[binding.bodyBindingId]!
            bodyTopologyAssets[binding.bodyBindingId] = {
              ...asset,
              referenceBrepResource: {
                cacheKey: sidecarEntry.sha256,
                encoding: sidecarEntry.encoding,
                payload: sidecarEntry.payload,
              },
            }
          }
          continue
        }
        registrationPlans.push({
          geometryGroup,
          resolvedEncoding: sidecarEntry.encoding,
          payload: {
            cacheKey: sidecarEntry.sha256,
            payload: sidecarEntry.payload,
            encoding: sidecarEntry.encoding,
            registerGeometryState: {
              objectId: geometryGroup.geometryObjectId,
              name: resolveImportedBrepGeometryName(topologySource, primaryBinding, file.fileId),
              bodyBindingId: primaryBinding.bodyBindingId,
              bodyId: primaryBinding.bodyId,
              meshId: primaryBinding.meshId,
              sourceFileId: file.fileId,
              sourceFileUuid: file.uuid,
              sourceLevel: primaryBinding.sourceLevel,
              brepKey: primaryBinding.brepRef.key,
              brepSha256: primaryBinding.brepRef.sha256,
              brepBodyOrdinal: primaryBinding.brepBodyOrdinal,
              brepBodyKind: primaryBinding.brepBodyKind,
              exportDefinitionId: geometryGroup.exportDefinitionId,
            },
          },
        })
      } catch (error) {
        appendImportedBrepHydrationFailure(
          failures,
          options.logger,
          `导入 BRep sidecar entry 预校验失败: ${formatImportedTopologyHydrationDiagnostic({
            file,
            topologySource,
            binding: primaryBinding,
            reason: `${resolveImportedTopoShapeRegistrationContext(
              primaryBinding,
              topoHydrationResult,
            )}; encoding=${resolvedBrepEncoding ?? '未声明'}`,
            topologySidecarFileId: resolveImportedTopologyDiagnosticFileId(file, primaryBinding),
            brepSidecarFileId: resolveImportedBrepSidecarFileId(file.fileId, primaryBinding.brepRef),
          })}`,
          error,
        )
      }
    }
  }

  if (failures.length > 0) {
    throw new ImportedBrepHydrationError(failures)
  }

  assertImportedBrepHydrationCurrent(options.assertCurrent)
  let hasCommittedPreparedResults = false

  /**
   * 在 Worker 批次仍可补偿时校验响应并一次提交 topo 运行时。
   *
   * @param preparedResults Worker 准备完成、尚待主线程确认的注册结果。
   */
  const commitPreparedResults = (
    preparedResults: readonly RegisterImportedBrepShapeResult[],
  ): void => {
    // 这是 Worker await 之后的 current guard；失败会让 active registrar 补偿、staged 宿主 abort。
    assertImportedBrepHydrationCurrent(options.assertCurrent)
    if (preparedResults.length !== registrationPlans.length) {
      throw new Error(
        `Worker BRep 原子注册结果数量不匹配: expected=${registrationPlans.length}; actual=${preparedResults.length}`,
      )
    }
    preparedResults.forEach((registrationResult, registrationIndex) => {
      const expectedObjectId = registrationPlans[registrationIndex]?.geometryGroup.geometryObjectId
      const runtimeObjectId = registrationResult.geometryObjectId.trim()
      const requestedObjectId = registrationResult.requestedGeometryObjectId?.trim()
        || runtimeObjectId
      if (!runtimeObjectId || requestedObjectId !== expectedObjectId) {
        throw new Error(
          'Worker BRep 原子注册结果对象 ID 不匹配: '
          + `index=${registrationIndex}; expected=${expectedObjectId}; `
          + `requested=${requestedObjectId || '缺失'}; actual=${runtimeObjectId || '缺失'}`,
        )
      }
    })

    // Worker 批次与结果契约均通过后，才把权威几何 ID 写入暂存 topo 资产并一次发布。
    registrationPlans.forEach(({ geometryGroup }, registrationIndex) => {
      const geometryObjectId = preparedResults[registrationIndex]!.geometryObjectId
      geometryGroup.bindings.forEach((brepBinding) => {
        const currentAsset = bodyTopologyAssets[brepBinding.bodyBindingId]
        if (!currentAsset) {
          return
        }
        bodyTopologyAssets[brepBinding.bodyBindingId] = {
          ...currentAsset,
          geometryObjectId,
          exportDefinitionId: geometryGroup.exportDefinitionId,
        }
      })
    })

    applyImportedModelTopologyRuntime(topologySource, bodyTopologyAssets)
    topologySource.bodyEntries = enrichBodyEntriesWithModelBodyIdentities(
      topologySource.bodyEntries,
      topologySource.bodyBrepBindings ?? {},
      bodyTopologyAssets,
    )
    hasCommittedPreparedResults = true
  }

  let registrationResults: readonly RegisterImportedBrepShapeResult[] = []
  try {
    if (registrationPlans.length === 0) {
      commitPreparedResults([])
      return
    }
    const brepRegistrar = preflightBrepRegistrar ?? requireImportedBrepRegistrar(options)
    registrationResults = await brepRegistrar.registerImportedBrepShapesAtomically(
      registrationPlans.map((registrationPlan) => registrationPlan.payload),
      { commitPreparedResults },
    )
    // 自定义 registrar 可能仍实现旧的单参数函数；保留 post-await guard 与统一提交作为兼容边界。
    if (!hasCommittedPreparedResults) {
      commitPreparedResults(registrationResults)
    }
  } catch (error) {
    // stale/current 错误必须保持原分类，供 staged runtime 宿主执行 abort，不能包装成 hydration 失败。
    assertImportedBrepHydrationCurrent(options.assertCurrent)
    const errorMessage = error instanceof Error ? error.message : String(error)
    const failedPlan = registrationPlans.find((registrationPlan) => (
      errorMessage.includes(registrationPlan.geometryGroup.geometryObjectId)
    )) ?? registrationPlans[0]
    if (failedPlan) {
      const { primaryBinding } = failedPlan.geometryGroup
      appendImportedBrepHydrationFailure(
        failures,
        options.logger,
        `导入 BRep Shape 原子注册失败: ${formatImportedTopologyHydrationDiagnostic({
          file,
          topologySource,
          binding: primaryBinding,
          reason: `${resolveImportedTopoShapeRegistrationContext(
            primaryBinding,
            topoHydrationResult,
          )}; encoding=${failedPlan.resolvedEncoding || '未声明'}`,
          topologySidecarFileId: resolveImportedTopologyDiagnosticFileId(file, primaryBinding),
          brepSidecarFileId: resolveImportedBrepSidecarFileId(file.fileId, primaryBinding.brepRef),
        })}`,
        error,
      )
    } else {
      appendImportedBrepHydrationFailure(
        failures,
        options.logger,
        `导入 BRep Shape 原子注册失败: fileId=${file.fileId}`,
        error,
      )
    }
    throw new ImportedBrepHydrationError(failures)
  }
}
